import { EditHomeworkParams, HomeworkSubmissionParams, IGetHomeworkUploadUrl } from '@/stores/group/types'
import { dictionaryWithKeys } from '@/utils'
import { Endpoints } from '../endpoints'
import { INetworkConfig, Instance } from '../instance'
import { IResponse } from '../types'
import {
  AddHomeworkParams,
  FileInformation,
  GetHomeworkParams,
  HomeworkDbRequestParams,
  IExamInfo,
  IExamResult,
  IGetGroupChapters,
  IGroupChapters,
  IGroupInfoData,
  IGroupInfoParams,
  IGroupLessonInfo,
  IGroupLessonParams,
  IGroupLessonVideoNameResponse,
  IGroupLessonVideos,
  IGroupParams,
  IGroupRatingParams,
  IGroupsData,
  IHomeworkChatCome,
  IHomeworkChatsGet,
  ILessonVideos,
  ISuccessResponse,
  LastVideoParamsType,
  TGetHomeworkFileUrlRes,
} from './types'

const config: INetworkConfig = {
  baseURL: Endpoints.Base,
}

class GroupApi extends Instance {
  constructor(config: INetworkConfig) {
    super(config)
  }

  getGroups = (params: IGroupParams): Promise<IResponse<IGroupsData>> => this.get(Endpoints.Groups, { params })

  getGroupInfo = (params: IGroupInfoParams): Promise<IResponse<IGroupInfoData>> =>
    this.get(dictionaryWithKeys(Endpoints.GroupInfo, { id: params.id }), {
      params: {
        page: params?.page,
        perPage: params?.perPage,
        staffId: params?.staffId,
      },
    })

  getGroupChapters = (params: IGetGroupChapters): Promise<IResponse<IGroupChapters>> => {
    const { id, ...data } = params

    return this.get(`${Endpoints.LessonsByGroup}/${id}`, { params: data })
  }

  getGroupLesson = (params: IGroupLessonParams): Promise<IResponse<IGroupLessonVideos>> =>
    this.get(dictionaryWithKeys(Endpoints.GroupLesson, { id: params?.groupId }), {
      params: {
        staffId: params?.staffId,
        perPage: 1000,
      },
    })

  getLessonVideos = (id: number): Promise<IResponse<ILessonVideos[]>> =>
    this.get(dictionaryWithKeys(Endpoints.LessonVideos, { id }))

  editHomework = (params: EditHomeworkParams): Promise<IResponse<IHomeworkChatsGet>> =>
    this.put(dictionaryWithKeys(Endpoints.LessonHomeworkChats, { homeworkId: params?.homeworkId }), {
      message: params?.message,
      attachmentIds: params?.attachmentIds,
    })

  lessonRatingSend = (params: IGroupRatingParams): Promise<IResponse<ISuccessResponse>> =>
    this.post(dictionaryWithKeys(Endpoints.GroupLessonRating, { id: params?.lessonId }), {
      rating: params?.rating,
      comment: params?.comment,
    })

  getHomeworkChat = (params: GetHomeworkParams): Promise<IResponse<IHomeworkChatsGet>> =>
    this.get(dictionaryWithKeys(Endpoints.LessonHomeworkChats, { homeworkId: params?.homeworkId }))

  sendHomework = (params: AddHomeworkParams): Promise<IResponse<{ id: number }>> =>
    this.post(dictionaryWithKeys(Endpoints.LessonHomeworkChats, { homeworkId: params?.homeworkId }), {
      message: params?.message || '',
      attachmentIds: params?.attachmentIds || [],
    })

  sendHomeworkDb = (params: HomeworkDbRequestParams): Promise<IResponse<ISuccessResponse>> => {
    const { homeworkId, homeworkChatId, submissions } = params

    return this.put(dictionaryWithKeys(Endpoints.HomeworkDbSubmissions, { homeworkId, homeworkChatId }), {
      submissions,
    })
  }
  // sendHomeworkDb = (): Promise<IResponse<IHomeworkChatsGet>> =>
  //   uploadHomeworkFile = (params: FormData): Promise<IResponse<IUploadHomewok[]>> =>
  //     this.post(Endpoints.HomeworkDbSubmissions, params, {
  //       headers: {
  //         'Content-Type': 'multipart/form-data',
  //       },
  //     })

  getHomeworkUrl = (params: IGetHomeworkUploadUrl): Promise<IResponse<TGetHomeworkFileUrlRes>> =>
    this.post(dictionaryWithKeys(Endpoints.HomeworkAttachmentUrl, { homeworkId: params.homeworkId }), {
      fileOrigName: params?.fileOrigName,
    })

  addTusAttachments = (params: FileInformation[]): Promise<IResponse<FileInformation[]>> =>
    this.post(dictionaryWithKeys(Endpoints.HomeworkTusAttachment, { homeworkId: params?.[0]?.homeworkId || 0 }), {
      attachments: params,
    })

  getExamResults = (params: number): Promise<IResponse<IExamResult[]>> =>
    this.get(`${Endpoints.Exams}/${params}${Endpoints.Students}`)

  // sendHomework = (params: IHomeworkSend): Promise<IResponse<MakeHomeworkRes>> =>
  //   this.post(Endpoints.HomeworkSend, params)

  getGroupLessonVideoName = (id: number): Promise<IResponse<IGroupLessonVideoNameResponse[]>> =>
    this.get(`${Endpoints.LessonVideoNameByGroup}/${id}`)

  getExam = (params: number): Promise<IResponse<IGroupLessonInfo>> => this.get(`${Endpoints.Exams}/${params}`)

  getExamChats = (params: number): Promise<IResponse<IHomeworkChatCome[]>> =>
    this.get(`${Endpoints.Exams}/${params}${Endpoints.Chats}`)

  sendExam = (params: IExamInfo): Promise<IResponse<ISuccessResponse>> =>
    this.post(`${Endpoints.Exams}/${params?.id}${Endpoints.Chats}${Endpoints.Submit}`, params.data)

  getLastVideo = (params: LastVideoParamsType) => this.post<IResponse<boolean>>(Endpoints.LastVideo, params)

  sendHomeworkSubmission = (params: HomeworkSubmissionParams): Promise<IResponse<ISuccessResponse>> =>
    this.put(`${Endpoints.HomeworkChatSubmissions}`, params)
}

export const groupApi = new GroupApi(config)
